import { AssetSummaryDto, UptimeAssetDto } from "../dtos/assetDto";
import { Asset, AssetConnectStatus, AssetDetails, AssetGeoLocation, AssetManualMetrics, AssetMetrics, AssetModel, AssetPlanList, MailaAsset, SummaryAsset } from "../models/asset";
import ApiService from "../services/api";

export const fetchSummaryAssets = async (): Promise<SummaryAsset[]> => {
    const apiService = new ApiService();
    
    try {
        const response = await apiService.get<any>(`/assets/group/all`);
        if (response.status === 200) {
            return response.data;
        } else return [];
    } catch (error) {
        console.error(error);
        return [];
    }
}

export const fetchAssets = async (): Promise<Asset[]> => {
    const apiService = new ApiService();
    
    try {
        const response = await apiService.get<any>(`/assets/group/all`);
        if (response.status === 200) {
            return response.data.map(AssetSummaryDto);
        } else return [];
    } catch (error) {
        console.error(error);
        return [];
    }
}

export const fetchUptimeAssets = async (assetIds: string[]): Promise<AssetManualMetrics[]> => {
    const apiService = new ApiService();
    
    try {
        const response = await apiService.post<any>(
            `/assets/uptime`,
            { assetIds }
        );
        if (response.status === 200) {
            return response.data.map(UptimeAssetDto);
        } else return [];
    } catch (error) {
        console.error(error);
        return [];
    }
}

export const updateUptimeAsset = async (asset: Partial<AssetManualMetrics>): Promise<boolean> => {
    const apiService = new ApiService();
    
    try {
        const response = await apiService.put<any>(`/assets/uptime`, asset);
        return response.status === 200;
    } catch (error) {
        console.error(error);
        return false;
    }
}

export const fetchAssetsConnectionStatus = async (assetIds: string[]): Promise<AssetConnectStatus[]> => {
    const apiService = new ApiService();

    try {
        const response = await apiService.post<AssetConnectStatus[]>(`/assets/connection-status`, { assetIds });
        if (response.status === 200) {
            return response.data;
        } else return [];
    } catch (error) {
        console.error(error);
        return [];
    }
}

export const fetchAssetsPlans = async (assetIds: string[]): Promise<AssetPlanList[]> => {
    const apiService = new ApiService();

    try {
        const response = await apiService.post<any[]>(`/assets/plans`, { assetIds });
        if (response.status === 200) {
            return response.data.map(assetPlan => ({
                assetId: assetPlan.assetId,
                plan: (assetPlan.plan ?? []).map((plan: any) => ({ id: plan.productId, name: plan.productName }))
            }));
        } else return [];
    } catch (error) {
        console.error(error);
        return [];
    }
}

export const fetchAssetGeoLocation = async (assetId: string): Promise<AssetGeoLocation | null> => {
    const apiService = new ApiService();

    try {
        const response = await apiService.get<any>(`/assets/${assetId}/geo-location`);
        if (response.status === 200) {
            return response.data;
        } else return null;
    } catch (error) {
        console.error(error);
        return null;
    }
}

export const fetchAssetMetrics = async (assetIds: string[]): Promise<AssetMetrics[]> => {
    const apiService = new ApiService();

    try {
        const response = await apiService.post<any[]>(`/assets/metrics`, { assetIds });
        if (response.status === 200) {
            return response.data.map((metric: any) => ({
                assetId: metric.assetId,
                odometer: metric.vehicleMetrics?.odometer,
                lastOdometerUpdate: metric.vehicleMetrics?.dates?.odometer ? new Date(metric.vehicleMetrics?.metricDates?.odometer) : undefined,
                hourmeter: metric.vehicleMetrics?.hourMeter,
                lastHourmeterUpdate: metric.vehicleMetrics?.dates?.hourMeter ? new Date(metric.vehicleMetrics?.metricDates?.hourMeter) : undefined,
            }));
        } else return [];
    } catch (error) {
        console.error(error);
        return [];
    }
}

export const fetchComplementaryAssetsDetails = async (assetIds: string[]): Promise<{
    vehicleModel: string | undefined;
    id: string;
    accountName: string;
    licensePlate: string;
    accountAssetId: string;
    modelCode?: string;
    model?: string;
    saleDate?: string;
    volkstotal?: {
        contractNumber: string;
        modality: string;
        status?: string;
    } | null;
}[]> => {
    const apiService = new ApiService();

    if(assetIds.length === 0) return [];
    
    try {
        const response = await apiService.post<any>(
            `/assets/summary`,
            { assetIds }
        );
        if (response.status === 200) {
            const data = response.data;
            return data.map((el: any) => ({
                vehicleModel: el.model,
                id: el.id,
                accountName: el.accountName,
                licensePlate: el.licensePlate,
                accountAssetId: el.accountAssetId,
                modelCode: el.modelCode,
                model: el.model,
                saleDate: el.saleDate,
                volkstotal: el.volkstotal ? {
                    contractNumber: el.volkstotal.contractNumber,
                    modality: el.volkstotal.modality,
                    status: el.volkstotal.status,
                } : null,
            }));
        } else return [];
    } catch (error) {
        console.error(error);
        return [];
    }
}

export const fetchAssetModels = async (): Promise<AssetModel[]> => {
    const apiService = new ApiService();

    try {
        const response = await apiService.get<AssetModel[]>(`/assets/models`);
        if (response.status === 200) {
            return response.data;
        } else return [];
    } catch (error) {
        console.error(error);
        return [];
    }
}

export const fetchAssetDetailsByChassis = async (chassis: string): Promise<AssetDetails | null> => {
    const apiService = new ApiService();

    try {
        const response = await apiService.get<AssetDetails>(`/assets/chassis/${chassis}`);
        if (response.status === 200) {
            return response.data;
        } else return null;
    } catch (error) {
        console.error(error);
        return null;
    }
}

export const fetchMailaAssets = async (assetIds: string[]): Promise<MailaAsset[]> => {
    const apiService = new ApiService(import.meta.env.VITE_ALERTS_URL, 'rio-maintenance');
    try {
        const response = await apiService.post<{data: MailaAsset[]}>(
            `/vehicleStatus`,
            {
                "assetIds": assetIds,
                "tagIds": [],
                "startAt": new Date(new Date().setDate(new Date().getDate() - 1)).toISOString(),
                "endAt": new Date().toISOString(),
                "pageSize": 1000,
                "page": 0,
                "filterType": "NONE"
            }
        );
        
        return response.data?.data ?? [];
    } catch (error) {
        console.error(error);
        return [];
    }
}